import { useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTenantList } from '../../api/tenants';
import { can, useMe } from '../../auth/AuthContext';
import { EmptyState, ErrorState, SkeletonTable } from '../../components/states';
import { DataTable } from '../../components/table/DataTable';
import { OnboardTenantModal } from '../../components/tenants/OnboardTenantModal';
import {
  Alert,
  Badge,
  Button,
  PageHeader,
  Section,
  formatDate,
  statusLabel,
  statusTone,
} from '../../components/ui';
import { formatNumber } from '../../format';

const PAGE = 50;

/**
 * /staff/tenants — every tenant, newest first. Each row opens /tenant/<id>,
 * where the settings, accounts, domains and WhatsApp number live. Any staff
 * role can onboard one from here.
 */
export function Tenants() {
  const me = useMe();
  const location = useLocation();
  const [offset, setOffset] = useState(0);
  const [notice, setNotice] = useState<ReactNode>(
    (location.state as { notice?: string } | null)?.notice ?? null,
  );
  const [onboarding, setOnboarding] = useState(false);

  const tenants = useTenantList({ limit: PAGE, offset });
  const page = tenants.data;
  const canOnboard = can(me, 'tenants:write');

  return (
    <>
      <PageHeader
        title="Tenants"
        actions={
          canOnboard ? <Button onClick={() => setOnboarding(true)}>Onboard a tenant</Button> : null
        }
      />
      {notice ? <Alert kind="success">{notice}</Alert> : null}

      <Section
        title="All tenants"
        meta={page ? `${formatNumber(page.pagination.total)} · newest first` : undefined}
      >
        {tenants.isError ? (
          <ErrorState
            error={tenants.error}
            onRetry={() => tenants.refetch()}
            retrying={tenants.isFetching}
          />
        ) : null}
        {tenants.isPending ? <SkeletonTable rows={4} cols={4} /> : null}
        {page && page.data.length === 0 ? (
          <EmptyState
            title="No tenants yet"
            body="Tenants appear once the first organization is onboarded."
          />
        ) : null}
        {page && page.data.length > 0 ? (
          <DataTable
            label="Tenants"
            columns={[
              { key: 'name', header: 'Name' },
              { key: 'status', header: 'Status' },
              { key: 'manager', header: 'Account manager' },
              { key: 'created', header: 'Created', align: 'num' },
            ]}
          >
            {page.data.map((t) => (
              <tr key={t.id}>
                <td>
                  <Link to={`/tenant/${t.id}`} data-primary>
                    {t.name}
                  </Link>{' '}
                  <code>{t.slug}</code>
                </td>
                <td>
                  <Badge tone={statusTone(t.status)}>{statusLabel(t.status)}</Badge>
                </td>
                <td>
                  {t.accountManager ? (
                    t.accountManager.email
                  ) : (
                    <span className="muted">Unassigned</span>
                  )}
                </td>
                <td className="num">{formatDate(t.createdAt)}</td>
              </tr>
            ))}
          </DataTable>
        ) : null}
        {page && (offset > 0 || page.pagination.hasMore) ? (
          <div className="row-actions">
            <Button
              variant="ghost"
              className="btn--small"
              disabled={offset === 0 || tenants.isPlaceholderData}
              onClick={() => setOffset(Math.max(0, offset - PAGE))}
            >
              Newer
            </Button>
            <Button
              variant="ghost"
              className="btn--small"
              disabled={!page.pagination.hasMore || tenants.isPlaceholderData}
              onClick={() => setOffset(offset + PAGE)}
            >
              Older
            </Button>
          </div>
        ) : null}
      </Section>

      {canOnboard && onboarding ? (
        <OnboardTenantModal
          onClose={() => setOnboarding(false)}
          onDone={(t) => {
            setOnboarding(false);
            setNotice(
              t.tenantOwnerId ? (
                <>
                  Created <Link to={`/tenant/${t.id}`}>{t.name}</Link>; the account{' '}
                  <strong>{t.ownerEmail}</strong> is its owner.
                </>
              ) : t.ownerEmail ? (
                <>
                  Created <Link to={`/tenant/${t.id}`}>{t.name}</Link>;{' '}
                  <strong>{t.ownerEmail}</strong> becomes its owner on signing up.
                </>
              ) : (
                <>
                  Created <Link to={`/tenant/${t.id}`}>{t.name}</Link> without an owner.
                </>
              ),
            );
          }}
        />
      ) : null}
    </>
  );
}
